<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watchEffect } from "vue";
import { marked } from "marked";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

import { toast } from "@/common/utils/toast";
import { paginateText, getMarkdownBlockHeights } from "@/common/utils/text";

import { analysisPDF } from "@/common/api/rest/analysis";
import type { AnalysisFullType } from "@/common/types/analysis";

import { pageExport } from "@/components";

interface ContentType {
  title: string | null;
  type: string;
  break: boolean;
  content: any;
}

interface PageType {
  page: number;
  content: {
    [key: string]: ContentType | null;
  } | null;
}

const props = defineProps<{
  data: AnalysisFullType;
  width: string;
  scale: number;
  footer?: any;
}>();

const { t, tm } = useI18n();

const route = useRoute();

const width = ref("210mm");
const height = ref(940);
const heightTitle = ref(52);

const analyse = ref<AnalysisFullType | null>(null);

const pages = ref<PageType[]>([]);

const summary = computed(
  () => tm("report.types.summary") as Record<string, string>
);

async function fetchData() {
  await analysisPDF(route.query.id as string, route.query.token as string)
    .then((response) => {
      const { success, data, message, code } = response;

      if (success) {
        analyse.value = data;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function paginate() {
  console.log("data", analyse.value);

  if (!analyse.value) return;

  pages.value = [];
  let PAGE = 0;
  let HEIGHT_INTERNAL = 0;

  pages.value.push({
    page: PAGE,
    content: {},
  });

  const paginateCompany = () => {
    if (!analyse.value!.company) return;

    HEIGHT_INTERNAL += heightTitle.value;

    const HEITHG_COMPANY_AVATAR = 90;
    let HEITHG_COMPANY = HEIGHT_INTERNAL + 0;

    const {
      avatar,
      name,
      document_name,
      document_number,
      address: addresses,
      address_id,
      contacts: contactsAll,
      contact_id,
    } = analyse.value!.company || {};

    pages.value[PAGE].content = {
      ...pages.value[PAGE].content,
      company: {
        title: null,
        type: "COMPANY",
        break: false,
        content: {
          avatar: avatar,
          name: null,
          document: null,
          address: null,
          contact: null,
        },
      },
    };

    const companyNameInfos = paginateText(name?.toUpperCase(), {
      width: "601px",
      height: height.value,
      font_size: "14px",
      line_height: "normal",
    });

    let documentText = "";

    if (document_name) documentText += document_name;
    if (document_number) documentText += ` ${document_number}`;

    const companyDocumentInfos = paginateText(documentText.toUpperCase(), {
      width: "601px",
      height: height.value,
      font_size: "14px",
      line_height: "normal",
    });

    const address = addresses?.filter(
      (item) => item.address_id === address_id
    )?.[0];

    let addressText = "";
    if (address?.address) addressText += address.address;
    if (address?.number) addressText += `, ${address.number}`;
    if (address?.complement) addressText += `, ${address.complement}`;
    if (address?.district) addressText += `, ${address.district}`;
    if (address?.city) addressText += `, ${address.city}`;
    if (address?.state) addressText += `, ${address.state}`;
    if (address?.country) addressText += `, ${address.country}`;
    if (address?.cep) addressText += `, CEP ${address.cep}`;

    const companyAdressInfos = paginateText(addressText.toUpperCase(), {
      width: "601px",
      height: height.value,
      font_size: "12px",
      line_height: "normal",
    });

    const contacts = contactsAll?.filter(
      (item) => item.contact_id === contact_id
    )?.[0];

    let contactText = "";
    if (contacts?.name) contactText += contacts.name;
    if (contacts?.contact) contactText += ` - ${contacts.contact}`;

    const companyContactInfos = paginateText(contactText.toUpperCase(), {
      width: "601px",
      height: height.value,
      font_size: "12px",
      line_height: "normal",
    });

    const companyNameHeight = companyNameInfos
      .map((item) => item.height)
      .reduce((prev, next) => prev + next, 0);

    const companyDocumentHeight = companyDocumentInfos
      .map((item) => item.height)
      .reduce((prev, next) => prev + next, 0);

    const companyAdressHeight = companyAdressInfos
      .map((item) => item.height)
      .reduce((prev, next) => prev + next, 0);

    const companyContactHeight = companyContactInfos
      .map((item) => item.height)
      .reduce((prev, next) => prev + next, 0);

    const HEITHG_COMPANY_INFOS =
      companyNameHeight +
      3 +
      companyDocumentHeight +
      3 +
      companyAdressHeight +
      3 +
      companyContactHeight;

    const HEIGHT_TOTAL = Math.max(HEITHG_COMPANY_AVATAR, HEITHG_COMPANY_INFOS);

    HEITHG_COMPANY += HEIGHT_TOTAL;

    if (HEITHG_COMPANY > height.value) {
      HEIGHT_INTERNAL = HEITHG_COMPANY;
      PAGE++;
      pages.value[PAGE] = {
        page: PAGE,
        content: {},
      };

      pages.value[PAGE].content = {
        ...pages.value[PAGE].content,
        company: {
          title: null,
          type: "COMPANY",
          break: true,
          content: {
            avatar: null,
            name: null,
            document: null,
            address: null,
            contact: null,
          },
        },
      };
    } else {
      HEIGHT_INTERNAL = HEITHG_COMPANY + 0;
    }

    pages.value[PAGE].content!.company!.content.name = companyNameInfos[0].text;
    pages.value[PAGE].content!.company!.content.document =
      companyDocumentInfos[0].text;
    pages.value[PAGE].content!.company!.content.address =
      companyAdressInfos[0].text;
    pages.value[PAGE].content!.company!.content.contact =
      companyContactInfos[0].text;
  };

  const paginateEmployee = () => {
    if (!analyse.value?.employee) return;

    const newPage = () => {
      PAGE++;
      pages.value[PAGE] = {
        page: PAGE,
        content: {},
      };
      HEIGHT_INTERNAL = 0;

      pages.value[PAGE].content = {
        ...pages.value[PAGE].content,
        employee: {
          title: t("report.titles.employee"),
          type: "EMPLOYEE",
          break: false,
          content: "",
        },
      };
    };

    HEIGHT_INTERNAL += heightTitle.value;

    pages.value[PAGE].content = {
      ...pages.value[PAGE].content,
      employee: {
        title: t("report.titles.employee"),
        type: "EMPLOYEE",
        break: false,
        content: "",
      },
    };

    let markText = "";

    const employee = analyse.value.employee;

    markText += `- **${t("employee.labels.social_name")}:**  ${employee.social_name}\n`;
  };

  const paginateMethod = () => {
    if (!analyse.value!.methods?.length) return;

    HEIGHT_INTERNAL += heightTitle.value;

    const newPage = () => {
      PAGE++;
      pages.value[PAGE] = {
        page: PAGE,
        content: {},
      };
      HEIGHT_INTERNAL = 0;
      // paginateCompany();

      pages.value[PAGE].content = {
        ...pages.value[PAGE].content,
        methods: {
          title: t("report.titles.methods"),
          type: "METHODS",
          break: false,
          content: [],
        },
      };
    };

    // INITILIZE PAGE
    pages.value[PAGE].content = {
      ...pages.value[PAGE].content,
      methods: {
        title: t("report.titles.methods"),
        type: "METHODS",
        break: false,
        content: "",
      },
    };

    let markText = "";

    analyse.value!.methods.forEach((item, index) => {
      markText += `#### ${item.code}\n`;
      markText += `${item.name}\n`;

      item.metadata?.forEach((field: any) => {
        if (field.type === "RANGE") {
          markText += `- **${field.name}:**  `;
          markText += `${t("question.labels.minimum")} ${field.min} ${field.measure}`;
          markText += "  |  ";
          markText += `${t("question.labels.maximum")} ${field.max} ${field.measure}\n`;
        } else {
          markText += `- **${field.name}:**  ${field.answer}\n`;
        }
      });

      if ((analyse.value.methods?.length ?? 0) > index + 1) {
        markText += "\n";
        markText += "---\n";
        markText += "\n";
      }
    });

    let methodText = "";
    markText.split("\n").forEach((text) => {
      HEIGHT_INTERNAL += 2;
      const pragraph = getMarkdownBlockHeights(text, {
        width: "617px",
        font_size: "14px",
        line_height: "normal",
      });

      if (HEIGHT_INTERNAL + pragraph[0]?.height > height.value) {
        pages.value[PAGE]!.content!.methods!.content = marked(methodText);
        newPage();
        HEIGHT_INTERNAL += pragraph[0]?.height ?? 0;
        methodText = `${text}\n`;
      } else {
        HEIGHT_INTERNAL += pragraph[0]?.height ?? 0;
        methodText += `${text}\n`;
      }
    });

    if (methodText.length) {
      pages.value[PAGE]!.content!.methods!.content = marked(methodText);
    }
  };

  paginateCompany();
  console.log("height-company", HEIGHT_INTERNAL);
  paginateEmployee();
  console.log("height-employee", HEIGHT_INTERNAL);
  paginateMethod();
  console.log("height-method", HEIGHT_INTERNAL);

  console.log("pages", pages.value);
}

watchEffect(() => {
  paginate();
});

onBeforeUnmount(() => {
  document.body.style.background = "#e4e4e4";
});

onMounted(() => {
  document.body.style.background = "white";
  fetchData();
});
</script>

<template>
  <template v-for="page in pages" :key="page.page">
    <page-export
      :width="width"
      :page="page.page + 1"
      :scale="scale"
      :show-footer="footer?.visible"
      :footer-title="footer?.title"
      :footer-subtitle="footer?.subtitle"
    >
      <div class="page-body">
        <template
          v-for="(item, index) of Object.values(page.content!)"
          :key="index"
        >
          <template v-if="item!.title">
            <div class="page-title">
              {{ item!.title }}
            </div>
          </template>

          <template v-if="item!.type === 'COMPANY'">
            <div class="page-content">
              <div class="company">
                <template v-if="item!.content.avatar">
                  <div class="brand">
                    <img :src="item!.content.avatar.base64" />
                  </div>
                </template>
                <div class="infos">
                  <template v-if="item!.content.name">
                    <div class="name">
                      {{ item!.content.name }}
                    </div>
                  </template>
                  <template v-if="item!.content.document">
                    <div class="document">
                      {{ item!.content.document }}
                    </div>
                  </template>
                  <template v-if="item!.content.address">
                    <div class="address">
                      {{ item!.content.address }}
                    </div>
                  </template>
                  <template v-if="item!.content.contact">
                    <div class="address">
                      {{ item!.content.contact }}
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </template>

          <template v-if="item!.type === 'EMPLOYEE'">
            <div class="page-content">
              <div class="employee">
                <div v-html="item!.content" />
              </div>
            </div>
          </template>

          <template v-if="item!.type === 'METHODS'">
            <div class="page-content">
              <div class="methods">
                <div v-html="item!.content" />
              </div>
            </div>
          </template>
        </template>
      </div>
    </page-export>
  </template>
</template>

<style lang="scss" scoped>
body {
  background: white !important;
}

.page-body {
  display: flex;
  flex-direction: column;
  margin-block-start: 2em;
  margin-inline: 2em;
  background: white;

  .page-title {
    display: flex;
    align-items: center;
    width: inherit;
    height: 2.25em;
    max-height: 2.25em;
    padding-inline-start: 1em;
    margin-block-end: 0.75em;
    background: var(--white-off);
    color: black;
    font-size: 0.875em;
    font-weight: bold;
    text-transform: uppercase;
  }

  .page-content {
    margin-block-end: 2em;
    padding-inline: 1em;
  }

  .company {
    display: flex;
    flex-direction: row;
    gap: 1em;

    .brand {
      min-width: 5em;
      width: 5em;
      height: 5em;

      img {
        width: 100%;
        height: auto;
      }
    }

    .infos {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 0.2rem;

      .name {
        font-size: 0.875em;
      }

      .document {
        font-size: 0.75em;
        font-weight: bold;
      }

      .address {
        font-size: 0.75em;
        text-transform: uppercase;
      }
    }
  }

  .employee {
    display: flex;
    flex-direction: column;
    gap: 1em;
    font-size: 0.875em;
  }

  .methods {
    display: flex;
    flex-direction: column;
    gap: 1em;
    font-size: 0.875em;
  }
}
</style>
